import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import PageHeader from '../components/PageHeader';
import SyncBars from '../components/SyncBars';
import { companyInfo } from '../mock';
import { useLocale } from '../i18n/LocaleContext';

export default function ContactPage() {
  const { toast } = useToast();
  const { t } = useLocale();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/contact`, formData);
      toast({
        title: 'Message sent',
        description: "Thanks for reaching out — we'll reply within one business day.",
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Your message could not be sent. Please try again or email us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const details = [
    { icon: Mail, label: t('contact.email'), value: companyInfo.email, href: `mailto:${companyInfo.email}` },
  ];

  return (
    <>
      <PageHeader
        index="// 05"
        eyebrow={t('contact.eyebrow')}
        title={t('contact.title')}
        accent={t('contact.accent')}
        intro={t('contact.intro')}
      />

      <section className="relative py-20 bg-surface overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bloom rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <SyncBars count={4} className="h-4" />
                  <span className="eyebrow">{t('contact.reachUs')}</span>
                </div>
                <h2 className="font-display text-3xl font-bold text-strong mb-3">{t('contact.letsConnect')}</h2>
                <p className="text-soft leading-relaxed max-w-md">
                  {t('contact.blurb')}
                </p>
              </div>

              <div className="space-y-3">
                {details.map((d) => {
                  const Icon = d.icon;
                  const inner = (
                    <Card className="bg-raise border-line hover:border-purple-500/40 transition-all duration-300">
                      <CardContent className="p-5 flex items-center gap-4">
                        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shrink-0">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-mono-ui text-[11px] text-faint tracking-wider">{d.label}</p>
                          <p className="text-strong font-medium">{d.value}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                  return d.href ? (
                    <a key={d.label} href={d.href} className="block">{inner}</a>
                  ) : (
                    <div key={d.label}>{inner}</div>
                  );
                })}
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="bg-raise border-line">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block font-mono-ui text-[11px] tracking-wider text-soft mb-2 uppercase">
                        {t('contact.yourName')}
                      </label>
                      <Input
                        id="name" name="name" type="text" required
                        value={formData.name} onChange={handleChange}
                        className="bg-raise border-line text-strong placeholder:text-faint focus:border-purple-500"
                        placeholder="Jane Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-mono-ui text-[11px] tracking-wider text-soft mb-2 uppercase">
                        {t('contact.yourEmail')}
                      </label>
                      <Input
                        id="email" name="email" type="email" required
                        value={formData.email} onChange={handleChange}
                        className="bg-raise border-line text-strong placeholder:text-faint focus:border-purple-500"
                        placeholder="jane@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block font-mono-ui text-[11px] tracking-wider text-soft mb-2 uppercase">
                        {t('contact.message')}
                      </label>
                      <Textarea
                        id="message" name="message" required rows={5}
                        value={formData.message} onChange={handleChange}
                        className="bg-raise border-line text-strong placeholder:text-faint focus:border-purple-500 resize-none"
                        placeholder="Tell us about your project..."
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 rounded-xl transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-strong" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {t('contact.sending')}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          {t('contact.send')}
                          <Send className="ml-2 w-5 h-5" />
                        </span>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
